/**
 * Section « Marqueurs » — pose des évènements et lecture de leur effet.
 *
 * ── Ce que cette page ajoute aux graphiques ─────────────────────────────────
 * Les traits posés sur les courbes répondent à « qu'est-ce qui s'est passé
 * ici ». Cette page répond à la question inverse, la seule qui compte quand on
 * vient de sortir une mise à jour : « ce changement-là, il a servi à quoi ? »
 * — en confrontant le même marqueur à quatre métriques d'un coup.
 *
 * Le tableau lui-même est partagé avec la vue d'ensemble
 * (`MarkerRecapTable`) : ici en entier et avec les boutons de modification,
 * là-bas tronqué et en lecture seule.
 *
 * ── Pourquoi pas SectionShell ───────────────────────────────────────────────
 * La coquille commune masque son contenu tant que les statistiques n'ont pas
 * répondu. Ici ce serait un contresens : poser ou corriger un marqueur ne
 * dépend pas des statistiques, et le formulaire doit rester utilisable pendant
 * que le récapitulatif charge, ou même s'il échoue.
 *
 * ── La réserve, écrite en bas de page et pas seulement ici ──────────────────
 * Les colonnes comparent une moyenne journalière avant à une moyenne
 * journalière après. Ce n'est PAS une mesure causale : rien ne distingue
 * l'effet d'une mise à jour de celui d'une campagne lancée la même semaine,
 * ni d'une vague de vacances scolaires. La page dit dans quel sens les
 * chiffres ont bougé, et s'abstient dès qu'elle n'a pas le recul pour le dire.
 */

import { useState } from 'react';

import { Card } from '../components/Card';
import { Layout } from '../components/Layout';
import { MarkerForm } from '../components/MarkerForm';
import { MarkerRecapTable } from '../components/MarkerRecapTable';
import { PeriodPicker } from '../components/PeriodPicker';
import { useAuth } from '../hooks/useAuth';
import { useMarkerMutations } from '../hooks/useMarkers';
import { useMarkerRecap } from '../hooks/useMarkerRecap';
import { usePeriodParam } from '../hooks/usePeriodParam';
import { usePeriodStats } from '../hooks/usePeriodStats';
import { dataPeriod } from '../lib/dates';
import { formatDayLong } from '../lib/format';
import type { Marker } from '../types';

export function MarkersPage() {
  const { email, token, signOut } = useAuth();
  const [period, setPeriod] = usePeriodParam();

  const { data: stats, error: statsError, isFetching } = usePeriodStats({
    from: period.from,
    to: period.to,
    token,
    onUnauthorized: signOut,
  });

  // Les verdicts se mesurent sur l'usage de l'app : données de la veille.
  const effective = dataPeriod(period, 'nightly');
  const recap = useMarkerRecap(stats, effective.from, effective.to);
  const mutations = useMarkerMutations();

  /** Marqueur en cours de modification, ou `null` en mode création. */
  const [editing, setEditing] = useState<Marker | null>(null);

  const handleDelete = async (marker: Marker): Promise<void> => {
    // `confirm` natif : une suppression est irreversible, et une boite de
    // dialogue maison pour trois mots ne protegerait pas mieux.
    if (!window.confirm(`Supprimer le marqueur « ${marker.label} » ?`)) return;
    if (editing?.id === marker.id) setEditing(null);
    await mutations.remove(marker.id);
  };

  return (
    <Layout email={email} lastSyncAt={stats?.meta.lastSyncAt ?? null} onSignOut={signOut}>
      {/* Le selecteur de periode cadre le RECAPITULATIF, pas la liste : un
          marqueur existe hors de toute periode, son effet ne se lit que dans
          une fenetre qui l'entoure. */}
      <PeriodPicker value={period} onChange={setPeriod} scope="nightly" />

      {/* `key` : changer de marqueur a modifier REMONTE le formulaire, dont
          l'etat repart alors des bonnes valeurs. Sans ca, il faudrait un
          useEffect de synchronisation, qui ecraserait la saisie en cours a
          chaque rendu du parent. */}
      <MarkerForm
        key={editing?.id ?? 'new'}
        editing={editing}
        isSaving={mutations.isSaving}
        onCancel={() => setEditing(null)}
        onSubmit={async (input) => {
          if (editing) {
            await mutations.update(editing.id, input);
            setEditing(null);
          } else {
            await mutations.create(input);
          }
        }}
      />

      {recap.error && (
        <p className="notice notice--error" role="alert">
          {recap.error.message}
        </p>
      )}

      {statsError && (
        <p className="notice notice--error" role="alert">
          Les marqueurs sont modifiables, mais leur effet n'a pas pu être calculé :{' '}
          {statsError.message}
        </p>
      )}

      <Card
        title="Marqueurs posés"
        subtitle={`Effet mesuré sur la période affichée — ${formatDayLong(effective.from)} au ${formatDayLong(effective.to)}`}
      >
        {recap.isPending && <p className="notice">Chargement des marqueurs...</p>}

        {!recap.isPending && recap.rows.length === 0 && (
          <p className="alert-empty">
            Aucun marqueur. Posez-en un ci-dessus : il apparaîtra aussitôt sur les courbes
            datées de la vue d'ensemble et de la section abonnement.
          </p>
        )}

        {recap.rows.length > 0 && (
          <MarkerRecapTable
            rows={recap.rows}
            isRefetching={isFetching}
            renderActions={(marker) => (
              <span className="row-actions">
                <button
                  type="button"
                  className="btn btn--small"
                  onClick={() => setEditing(marker)}
                >
                  Modifier
                </button>
                <button
                  type="button"
                  className="btn btn--small"
                  disabled={mutations.isSaving}
                  onClick={() => void handleDelete(marker)}
                >
                  Supprimer
                </button>
              </span>
            )}
          />
        )}
      </Card>

      <p className="footnote">
        <strong>Un avant/après, pas une preuve.</strong> Chaque colonne compare la moyenne
        journalière des 14 jours précédant le marqueur à celle des 14 jours suivants, le jour du
        marqueur inclus. Rien n'y distingue l'effet du changement de celui d'une campagne lancée
        la même semaine, d'une vague de vacances scolaires ou d'une saisonnalité. Un écart de
        moins de 5 % s'affiche « stable » : d'une quinzaine à l'autre, l'activité bouge de
        quelques pourcents sans qu'il se soit rien passé. Et un verdict n'est publié qu'avec au
        moins 5 jours mesurés de chaque côté — un marqueur au bord de la période affichée, ou
        hors d'elle, reste donc à « — » jusqu'à ce qu'on élargisse la période. Les deux colonnes
        de cumul (« Comptes », « Abonnés ») comparent des PENTES, pas des totaux : c'est le
        nombre gagné par jour, sinon le verdict serait toujours « en hausse ».
      </p>
    </Layout>
  );
}
